import { useEffect, useState } from 'react';
import type {
  ArchiveRecord,
  LocationData,
  UpdateMetadataRequest,
} from '../types/moderation';
import { ApiError, updateMetadata } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { toast } from '../lib/toast';
import InternalNotesEditor from './InternalNotesEditor';
import MetadataLocationFieldset from './MetadataLocationFieldset';
import Checkbox from './ui/Checkbox';
import HelpHint from './HelpHint';

interface MetadataEditorProps {
  record: ArchiveRecord;
  onSaved: (record: ArchiveRecord) => void;
  section?: 'metadata' | 'notes';
}

type Precision = LocationData['precision'];

interface FormState {
  title: string;
  description: string;
  themes: string;
  communities: string;
  decades: string;
  yearStart: string;
  yearEnd: string;
  sensitive: boolean;
  sensitivityNote: string;
  internalNotes: string;
  featured: boolean;
  sourceUrl: string;
  locationText: string;
  locationLat: string;
  locationLng: string;
  locationPrecision: Precision;
}

function fromRecord(record: ArchiveRecord): FormState {
  return {
    title: record.title || '',
    description: record.description || '',
    themes: (record.themes || []).join(', '),
    communities: (record.communities || []).join(', '),
    decades: (record.decades || []).join(', '),
    yearStart: record.yearStart || '',
    yearEnd: record.yearEnd || '',
    sensitive: Boolean(record.sensitive),
    sensitivityNote: record.sensitivityNote || '',
    internalNotes: record.internalNotes || '',
    featured: Boolean(record.featured),
    sourceUrl: record.sourceUrl || '',
    locationText: record.location?.text || '',
    locationLat:
      typeof record.location?.lat === 'number' ? String(record.location.lat) : '',
    locationLng:
      typeof record.location?.lng === 'number' ? String(record.location.lng) : '',
    locationPrecision: record.location?.precision || 'exact',
  };
}

function parseCoord(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : undefined;
}

/**
 * F-103 -- shallow value comparison that handles the FormState shape
 * (all strings + a handful of booleans). Replaces the previous
 * `JSON.stringify(initial) !== JSON.stringify(form)` dirty check which
 * is fragile across Node versions and silently collapses `undefined`
 * vs `''`.
 */
function statesEqual(a: FormState, b: FormState): boolean {
  return (
    a.title === b.title &&
    a.description === b.description &&
    a.themes === b.themes &&
    a.communities === b.communities &&
    a.decades === b.decades &&
    a.yearStart === b.yearStart &&
    a.yearEnd === b.yearEnd &&
    a.sensitive === b.sensitive &&
    a.sensitivityNote === b.sensitivityNote &&
    a.internalNotes === b.internalNotes &&
    a.featured === b.featured &&
    a.sourceUrl === b.sourceUrl &&
    a.locationText === b.locationText &&
    a.locationLat === b.locationLat &&
    a.locationLng === b.locationLng &&
    a.locationPrecision === b.locationPrecision
  );
}

/**
 * F-103 -- per-field validation. Returns an object whose keys are
 * FormState fields and values are user-facing error messages. An empty
 * object means valid.
 */
type FormErrors = Partial<Record<keyof FormState, string>>;

function validateForm(form: FormState): FormErrors {
  const errors: FormErrors = {};
  if (!form.title.trim()) {
    errors.title = 'Title is required.';
  }
  const yearRe = /^\d{4}$/;
  if (form.yearStart && !yearRe.test(form.yearStart.trim())) {
    errors.yearStart = 'Year must be a 4-digit number.';
  }
  if (form.yearEnd && !yearRe.test(form.yearEnd.trim())) {
    errors.yearEnd = 'Year must be a 4-digit number.';
  }
  if (
    form.yearStart &&
    form.yearEnd &&
    yearRe.test(form.yearStart.trim()) &&
    yearRe.test(form.yearEnd.trim()) &&
    Number(form.yearEnd) < Number(form.yearStart)
  ) {
    errors.yearEnd = 'End year cannot be earlier than start year.';
  }
  const latRaw = form.locationLat.trim();
  if (latRaw) {
    const n = Number(latRaw);
    if (!Number.isFinite(n) || n < -90 || n > 90) {
      errors.locationLat = 'Latitude must be a number between -90 and 90.';
    }
  }
  const lngRaw = form.locationLng.trim();
  if (lngRaw) {
    const n = Number(lngRaw);
    if (!Number.isFinite(n) || n < -180 || n > 180) {
      errors.locationLng = 'Longitude must be a number between -180 and 180.';
    }
  }
  return errors;
}

function buildLocation(state: FormState, record: ArchiveRecord): LocationData | undefined {
  const text = state.locationText.trim();
  const lat = parseCoord(state.locationLat);
  const lng = parseCoord(state.locationLng);
  const precision = state.locationPrecision;
  const empty = !text && lat === undefined && lng === undefined;
  if (empty && !record.location) return undefined;
  const next: LocationData = { text, precision };
  if (lat !== undefined) next.lat = lat;
  if (lng !== undefined) next.lng = lng;
  return next;
}

function toUpdatePayload(
  state: FormState,
  record: ArchiveRecord,
): Partial<UpdateMetadataRequest> {
  // Snapshot ID of the AI trace whose suggestions the moderator was
  // shown when they made this edit. Sent so the server-side outcome
  // telemetry credits accept/override counts to the right trace even
  // if the AI re-runs between display and save.
  const aiSuggestionsSnapshotId = (record.aiAnalysis || []).slice(-1)[0]?.id;
  return {
    title: state.title,
    description: state.description,
    themes: state.themes
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    communities: state.communities
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    decades: state.decades
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    yearStart: state.yearStart || undefined,
    yearEnd: state.yearEnd || undefined,
    sensitivity: state.sensitive,
    sensitivityNote: state.sensitivityNote,
    internalNotes: state.internalNotes,
    featured: state.featured,
    sourceUrl: state.sourceUrl,
    location: buildLocation(state, record),
    aiSuggestionsSnapshotId,
  };
}

function copy(value: string): void {
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    void navigator.clipboard.writeText(value);
  }
}

/**
 * F-308 — derive a YouTube / Vimeo thumbnail URL from a video URL.
 * Returns null when the URL is from an unsupported host.
 */
function videoThumbnail(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const v = u.searchParams.get('v');
      if (v) return `https://i.ytimg.com/vi/${encodeURIComponent(v)}/hqdefault.jpg`;
    }
    if (host === 'youtu.be') {
      const id = u.pathname.replace(/^\//, '');
      if (id) return `https://i.ytimg.com/vi/${encodeURIComponent(id)}/hqdefault.jpg`;
    }
    if (host === 'vimeo.com') {
      // Vimeo requires an oEmbed call for thumbnails; without async work,
      // we surface the URL as a recognisable link instead.
      return null;
    }
  } catch {
    // not a URL
  }
  return null;
}

interface ReadOnlySectionProps {
  title: string;
  defaultOpen?: boolean;
  testId?: string;
  children: React.ReactNode;
}

function ReadOnlySection({ title, defaultOpen = false, testId, children }: ReadOnlySectionProps) {
  return (
    <details className="metadata-section" open={defaultOpen} data-testid={testId}>
      <summary className="metadata-section__summary">{title}</summary>
      <div className="metadata-section__body">{children}</div>
    </details>
  );
}

interface RowProps {
  label: string;
  value?: React.ReactNode;
  empty?: string;
}

function Row({ label, value, empty = '—' }: RowProps) {
  const isEmpty =
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '');
  return (
    <div className="metadata-section__row">
      <span className="metadata-section__label">{label}</span>
      <span className="metadata-section__value">{isEmpty ? empty : value}</span>
    </div>
  );
}

function CopyableValue({ value }: { value: string }) {
  if (!value) return <>—</>;
  return (
    <span className="metadata-section__copyable">
      <code>{value}</code>
      <button
        type="button"
        className="metadata-section__copy-btn"
        onClick={() => copy(value)}
        aria-label={`Copy ${value}`}
      >
        Copy
      </button>
    </span>
  );
}

/**
 * F-214 — persist in-flight metadata drafts to localStorage so a Cmd-R
 * or tab close does not destroy the moderator's typed work. Keyed by
 * submission id under a versioned prefix so the existing localStorage
 * shape isn't affected.
 */
const DRAFT_KEY_PREFIX = 'moderation-ui:metadata-draft:';

function readDraft(submissionId: string): FormState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY_PREFIX + submissionId);
    if (!raw) return null;
    return JSON.parse(raw) as FormState;
  } catch {
    return null;
  }
}

function writeDraft(submissionId: string, draft: FormState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DRAFT_KEY_PREFIX + submissionId, JSON.stringify(draft));
  } catch {
    // ignore quota / private mode
  }
}

function clearDraft(submissionId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(DRAFT_KEY_PREFIX + submissionId);
  } catch {
    // ignore
  }
}

export default function MetadataEditor({ record, onSaved, section = 'metadata' }: MetadataEditorProps) {
  const [initial, setInitial] = useState<FormState>(() => fromRecord(record));
  const [form, setForm] = useState<FormState>(() => {
    // F-214: if a draft exists for this submission, prefer it over the
    // server record so a refresh restores the typed values.
    const draft = readDraft(record.submissionId);
    return draft || fromRecord(record);
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * F-104: when the server returns 409 we no longer wipe the user's
   * typed work. We hold the conflicting server record here and let
   * the moderator pick "Keep mine" / "Reload from server".
   */
  const [conflict, setConflict] = useState<ArchiveRecord | null>(null);

  useEffect(() => {
    const next = fromRecord(record);
    setInitial(next);
    setForm(next);
    // Clear any conflict tied to a previous record.
    setConflict(null);
  }, [record]);

  // F-214: warn the moderator before they navigate away with unsaved
  // changes. Pairs with the conflict banner so an accidental Cmd-R no
  // longer destroys a long edit.
  useEffect(() => {
    const dirtyNow = !statesEqual(initial, form);
    if (!dirtyNow) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers ignore the message and show their own copy.
      e.returnValue = 'Unsaved changes will be lost.';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [initial, form]);

  // F-214: persist the typed values so a refresh / nav-away can restore
  // them. Clear on save (handled in save()) so the draft doesn't stick.
  useEffect(() => {
    const dirtyNow = !statesEqual(initial, form);
    if (dirtyNow) {
      writeDraft(record.submissionId, form);
    } else {
      clearDraft(record.submissionId);
    }
  }, [record.submissionId, initial, form]);

  // F-103: structural dirty check + field validation.
  const dirty = !statesEqual(initial, form);
  const fieldErrors = validateForm(form);
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const save = async () => {
    if (hasFieldErrors) {
      // Surface validation errors instead of round-tripping to the API
      // and watching it return a generic 4xx.
      setError('Please fix the highlighted fields before saving.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const saved = await updateMetadata(
        record.submissionId,
        toUpdatePayload(form, record),
        record.updatedAt,
      );
      onSaved(saved);
      setInitial(fromRecord(saved));
      clearDraft(record.submissionId); // F-214
      toast.success('Changes saved.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // F-104: preserve the user's typed work. Surface the conflicting
        // server record via a banner with explicit "Reload from server"
        // and "Keep my edits" buttons; do NOT auto-replace `form`.
        const conflictBody = err.body as { record?: ArchiveRecord } | null;
        if (conflictBody?.record) {
          setConflict(conflictBody.record);
        }
        setError('Someone else updated this submission. Choose how to resolve.');
      } else {
        setError((err as Error).message || 'Save failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setForm(initial);
  };

  /** F-104: replace the form with the conflicting server record. */
  const reloadFromConflict = () => {
    if (!conflict) return;
    const next = fromRecord(conflict);
    setInitial(next);
    setForm(next);
    onSaved(conflict);
    setConflict(null);
    setError(null);
  };

  /** F-104: keep the user's typed work and dismiss the conflict banner. */
  const keepMyEdits = () => {
    setConflict(null);
    setError(null);
  };

  // F-102: the Notes tab now delegates to a dedicated InternalNotesEditor
  // so the Details and Notes tabs no longer hold two independent copies
  // of the same form state.
  if (section === 'notes') {
    return <InternalNotesEditor record={record} onSaved={onSaved} testIdSuffix="notes" />;
  }

  const validationWarnings = Array.isArray(
    (record.pipeline?.validation as { warnings?: unknown })?.warnings,
  )
    ? ((record.pipeline.validation as { warnings: string[] }).warnings)
    : [];

  // Latest AI suggestions (if any) for the "Apply suggestion" buttons next
  // to the editable fields. Buttons render only when the current form value
  // is empty AND the suggestion differs.
  const aiSuggestions = (record.aiAnalysis || []).slice(-1)[0]?.signals?.metadata;
  const renderSuggestion = (
    fieldKey: keyof FormState,
    suggestion: string | undefined,
    testId: string,
  ) => {
    if (!suggestion) return null;
    const current = String(form[fieldKey] ?? '').trim();
    if (current && current === suggestion.trim()) return null;
    if (current) return null;
    return (
      <button
        type="button"
        className="ai-suggestion-pill"
        onClick={() => update(fieldKey, suggestion as never)}
        data-testid={testId}
      >
        AI: {suggestion.length > 40 ? `${suggestion.slice(0, 40)}…` : suggestion}
      </button>
    );
  };

  return (
    <div className="metadata-editor" data-testid="metadata-editor">
      {conflict && (
        <div
          className="metadata-editor__conflict"
          role="alert"
          data-testid="metadata-conflict-banner"
        >
          <strong>Edit conflict.</strong>{' '}
          Another moderator saved changes while you were editing.
          Your typed values are still here.
          <div className="metadata-editor__conflict-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={reloadFromConflict}
              data-testid="metadata-conflict-reload"
            >
              Reload from server (discards your changes)
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={keepMyEdits}
              data-testid="metadata-conflict-keep"
            >
              Keep my edits
            </button>
          </div>
        </div>
      )}
      <label className="form-group">
        <span>
          Title
          <HelpHint topic="Title style">
            <p>
              Short, descriptive, specific. Good: "Rally and march outside
              Queens Park, 1994". Avoid generic titles ("Image",
              "Untitled") or restating the description.
            </p>
            <a href="#/guide#metadata-style">Read more →</a>
          </HelpHint>
        </span>
        <input
          type="text"
          value={form.title}
          onChange={(e) => update('title', e.target.value)}
          data-testid="field-title"
          aria-invalid={fieldErrors.title ? 'true' : undefined}
          aria-describedby={fieldErrors.title ? 'field-title-error' : undefined}
        />
        {fieldErrors.title && (
          <div id="field-title-error" className="field-error" role="alert" data-testid="field-title-error">
            {fieldErrors.title}
          </div>
        )}
        {renderSuggestion('title', aiSuggestions?.titleSuggestion, 'suggest-title')}
      </label>
      <label className="form-group">
        <span>
          Description
          <HelpHint topic="Alt text and descriptions">
            <p>
              Explain what the item is, why it matters, and any known
              context. For images, the AI also suggests separate alt text;
              keep alt text to a single accessible sentence and avoid
              repeating the title verbatim.
            </p>
            <a href="#/guide#accessibility">Read more →</a>
          </HelpHint>
        </span>
        <textarea
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
          rows={4}
          data-testid="field-description"
        />
        {renderSuggestion(
          'description',
          aiSuggestions?.descriptionSuggestion,
          'suggest-description',
        )}
      </label>
      <label className="form-group">
        <span>
          Themes (comma separated)
          <HelpHint topic="Themes">
            <p>
              Use the most specific terms from the allowed list (Community,
              Activism, Arts and Culture, Sport, Health, Nightlife,
              Organisations, Personal Stories, Milestones, Politics and
              Law). One to four is plenty. Avoid over-tagging.
            </p>
            <a href="#/guide#taxonomy-guidance">Read more →</a>
          </HelpHint>
        </span>
        <input
          type="text"
          value={form.themes}
          onChange={(e) => update('themes', e.target.value)}
          data-testid="field-themes"
        />
        {renderSuggestion(
          'themes',
          aiSuggestions?.themes?.join(', ') || undefined,
          'suggest-themes',
        )}
      </label>
      <label className="form-group">
        <span>
          Communities (comma separated)
          <HelpHint topic="Communities">
            <p>
              Community tags the contributor picked on the submission form.
              Use the tag names exactly as they appear there (for example
              Lesbian, Drag, Elders, Fair Day). A name that is not on the
              list is dropped when you save.
            </p>
          </HelpHint>
        </span>
        <input
          type="text"
          value={form.communities}
          onChange={(e) => update('communities', e.target.value)}
          data-testid="field-communities"
        />
      </label>
      <label className="form-group">
        <span>
          Decades (comma separated)
          <HelpHint topic="Decades">
            <p>
              Pick from Pre-1970s, 1970s, 1980s, 1990s, 2000s, 2010s, 2020s.
              One or two values. Use the decade even when an exact year is
              unknown.
            </p>
            <a href="#/guide#taxonomy-guidance">Read more →</a>
          </HelpHint>
        </span>
        <input
          type="text"
          value={form.decades}
          onChange={(e) => update('decades', e.target.value)}
          data-testid="field-decades"
        />
        {renderSuggestion(
          'decades',
          aiSuggestions?.decades?.join(', ') || undefined,
          'suggest-decades',
        )}
      </label>
      <MetadataLocationFieldset
        locationText={form.locationText}
        locationLat={form.locationLat}
        locationLng={form.locationLng}
        locationPrecision={form.locationPrecision}
        onChange={(field, value) => {
          if (field === 'locationPrecision') {
            update('locationPrecision', value as Precision);
          } else {
            update(field, value);
          }
        }}
        errors={{
          locationLat: fieldErrors.locationLat,
          locationLng: fieldErrors.locationLng,
        }}
      />
      <div className="form-row">
        <label className="form-group form-group-half">
          <span>
            Year start
            <HelpHint topic="Year range">
              <p>
                Use ISO year (YYYY) or YYYY-MM when a month is clear. Leave
                year-end blank for a single-year event. Use a range when the
                item spans years (e.g. an oral history covering 1985 to
                1992).
              </p>
              <a href="#/guide#metadata-style">Read more →</a>
            </HelpHint>
          </span>
          <input
            type="text"
            value={form.yearStart}
            onChange={(e) => update('yearStart', e.target.value)}
            data-testid="field-yearStart"
            aria-invalid={fieldErrors.yearStart ? 'true' : undefined}
            aria-describedby={fieldErrors.yearStart ? 'field-yearStart-error' : undefined}
          />
          {fieldErrors.yearStart && (
            <div id="field-yearStart-error" className="field-error" role="alert" data-testid="field-yearStart-error">
              {fieldErrors.yearStart}
            </div>
          )}
          {renderSuggestion('yearStart', aiSuggestions?.yearStart, 'suggest-yearStart')}
        </label>
        <label className="form-group form-group-half">
          <span>Year end</span>
          <input
            type="text"
            value={form.yearEnd}
            onChange={(e) => update('yearEnd', e.target.value)}
            data-testid="field-yearEnd"
            aria-invalid={fieldErrors.yearEnd ? 'true' : undefined}
            aria-describedby={fieldErrors.yearEnd ? 'field-yearEnd-error' : undefined}
          />
          {fieldErrors.yearEnd && (
            <div id="field-yearEnd-error" className="field-error" role="alert" data-testid="field-yearEnd-error">
              {fieldErrors.yearEnd}
            </div>
          )}
          {renderSuggestion('yearEnd', aiSuggestions?.yearEnd, 'suggest-yearEnd')}
        </label>
      </div>
      <label className="form-group">
        <span>Source URL (optional)</span>
        <input
          type="url"
          value={form.sourceUrl}
          onChange={(e) => update('sourceUrl', e.target.value)}
          placeholder="https://example.com/original-citation"
          data-testid="field-sourceUrl"
        />
      </label>
      <div className="metadata-editor__sensitivity-row">
        <Checkbox
          checked={form.sensitive}
          onCheckedChange={(v) => update('sensitive', v)}
          label="Sensitive content"
        />
        <HelpHint topic="Sensitive content">
          <p>
            Tick when the item is fine to publish but needs a content
            warning (grief, violence, slurs, health info, minors, explicit
            content). The submission still publishes; the warning gates
            display on the public site.
          </p>
          <a href="#/guide#sensitivity-and-privacy">Read more →</a>
        </HelpHint>
      </div>
      <label className="form-group">
        <span>Sensitivity note</span>
        <textarea
          value={form.sensitivityNote}
          onChange={(e) => update('sensitivityNote', e.target.value)}
          rows={2}
          data-testid="field-sensitivityNote"
        />
      </label>
      <Checkbox
        checked={form.featured}
        onCheckedChange={(v) => update('featured', v)}
        label="Featured"
      />
      <label className="form-group">
        <span>Internal notes</span>
        <textarea
          value={form.internalNotes}
          onChange={(e) => update('internalNotes', e.target.value)}
          rows={3}
          data-testid="field-internalNotes-main"
        />
      </label>
      {error && <div className="field-error" role="alert">{error}</div>}
      {hasFieldErrors && !error && (
        <div
          id="metadata-editor-field-summary"
          className="field-hint"
          data-testid="metadata-editor-field-summary"
        >
          Save is disabled because some fields need attention.
        </div>
      )}
      <div className="editor-actions">
        <button type="button" className="btn btn-secondary" onClick={cancel} disabled={!dirty || busy}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={save}
          disabled={!dirty || busy || hasFieldErrors}
          data-testid="metadata-save"
          aria-describedby={hasFieldErrors ? 'metadata-editor-field-summary' : undefined}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>

      <ReadOnlySection title="Contributor & consent" defaultOpen testId="section-contributor">
        <Row label="Name" value={record.contributor?.name} />
        <Row
          label="Email"
          value={
            record.contributor?.email ? (
              <a href={`mailto:${record.contributor.email}`}>{record.contributor.email}</a>
            ) : undefined
          }
        />
        <Row label="Attribution preference" value={record.contributor?.attributionPref} />
        <Row label="Copyright holder (from contributor)" value={record.copyrightHolder} />
        <Row label="Collection credit (from contributor)" value={record.collectionCredit} />
        <Row
          label="Consent given"
          value={record.consent?.timestamp ? formatDateTime(record.consent.timestamp) : undefined}
        />
        <Row label="Consent version" value={record.consent?.version} />
        <Row label="Consent IP" value={record.consent?.ip} />
        <Row
          label="Withdrawal token"
          value={record.withdrawalToken ? <CopyableValue value={record.withdrawalToken} /> : undefined}
        />
        <Row
          label="External video URL"
          value={
            record.externalVideoUrl ? (
              <span className="metadata-section__video">
                <a href={record.externalVideoUrl} target="_blank" rel="noreferrer">
                  {record.externalVideoUrl}
                </a>
                {(() => {
                  // F-308: inline YouTube thumbnail so moderators can
                  // verify the link works without switching tabs.
                  const thumb = videoThumbnail(record.externalVideoUrl);
                  if (!thumb) return null;
                  return (
                    <a
                      href={record.externalVideoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="metadata-section__video-thumb"
                      aria-label="Open external video"
                    >
                      <img
                        src={thumb}
                        alt=""
                        loading="lazy"
                        data-testid="external-video-thumb"
                      />
                    </a>
                  );
                })()}
              </span>
            ) : undefined
          }
        />
      </ReadOnlySection>

      <ReadOnlySection title="Pipeline status" defaultOpen testId="section-pipeline">
        <Row
          label="Status"
          value={
            record.pipeline?.status ? (
              <span className={`pipeline-status pipeline-status--${record.pipeline.status}`}>
                {record.pipeline.status}
              </span>
            ) : undefined
          }
        />
        {record.pipeline?.error && (
          <div className="metadata-section__error" role="alert" data-testid="pipeline-error">
            {record.pipeline.error}
          </div>
        )}
        <Row
          label="Completed at"
          value={record.pipeline?.completedAt ? formatDateTime(record.pipeline.completedAt) : undefined}
        />
        {validationWarnings.length > 0 && (
          <div className="metadata-section__row">
            <span className="metadata-section__label">Validation warnings</span>
            <ul className="metadata-section__warnings" data-testid="validation-warnings">
              {validationWarnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}
      </ReadOnlySection>

      <ReadOnlySection title="Submission identity" testId="section-identity">
        <Row label="Submission ID" value={<CopyableValue value={record.submissionId} />} />
        <Row label="Upload ID" value={<CopyableValue value={record.uploadId} />} />
        <Row
          label="WordPress post ID"
          value={
            typeof record.wpPostId === 'number' && record.wpPostId > 0
              ? record.wpPostId
              : undefined
          }
        />
        <Row label="Media type" value={record.mediaType} />
        <Row label="Submission mode" value={record.submissionMode} />
        <Row
          label="Created"
          value={record.createdAt ? formatDateTime(record.createdAt) : undefined}
        />
        <Row
          label="Updated"
          value={record.updatedAt ? formatDateTime(record.updatedAt) : undefined}
        />
      </ReadOnlySection>
    </div>
  );
}
